import { db } from "../firebase";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  arrayUnion
} from "firebase/firestore";
import { format } from "date-fns";

const BOOKINGS_COLLECTION = "bookings";
const SLOT_BOOKINGS_COLLECTION = "slotBookings";

/** Recursively strips `undefined` values — Firestore rejects any field
 * whose value is undefined (null is fine, undefined is not). Booking
 * payloads are deep/nested, so this is applied before every write rather
 * than trusting every caller to only ever pass fully-populated objects. */
function stripUndefined(value) {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (value && typeof value === "object" && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)]),
    );
  }
  return value;
}

function toDateStr(date) {
  if (typeof date === "string") return date;
  // Firestore returns Timestamps for saved dates; convert them first.
  const d = date?.toDate ? date.toDate() : new Date(date);
  if (Number.isNaN(d.getTime())) {
    throw new Error("A round date on this booking is invalid.");
  }
  return format(d, "yyyy-MM-dd");
}

function normalizeBookingDates(payload) {
  const normalize = (date) => (date == null ? date : toDateStr(date));
  const normalized = { ...payload };
  if (Array.isArray(payload.roundDates)) {
    normalized.roundDates = payload.roundDates.map(normalize);
  }
  for (const field of ["checkInDate", "checkOutDate", "startDate", "endDate"]) {
    if (Object.prototype.hasOwnProperty.call(payload, field)) {
      normalized[field] = normalize(payload[field]);
    }
  }
  return normalized;
}

function slotDocId(courseId, dateStr, time) {
  return `${courseId}_${dateStr}_${time}`;
}

export const newBookingId = () => doc(collection(db, BOOKINGS_COLLECTION)).id;

function collectSlots(data) {
  const {
    selectedCourses = [],
    roundDates = [],
    roundTimes = [],
    numPlayers = 1,
  } = data || {};
  const map = new Map();
  selectedCourses.forEach((course, i) => {
    const date = roundDates[i];
    const time = roundTimes[i];
    if (!course?.id || !date || !time) return;
    const dateStr = toDateStr(date);
    const id = slotDocId(course.id, dateStr, time);
    const prev = map.get(id);
    map.set(id, {
      courseId: course.id,
      name: course.name,
      dateStr,
      time,
      capacity:
        course.tee_slots?.find((s) => s.time === time)?.capacity ?? null,
      count: (prev?.count || 0) + Number(numPlayers || 1),
    });
  });
  return map;
}


/** Stamps the Golf Getaway Pass issue date (only called on the first send). */
export async function setPassIssuedAt(bookingId, issuedAt) {
  await updateDoc(doc(db, "bookings", bookingId), { passIssuedAt: issuedAt });
}

export async function updateBooking(bookingId, patch, historyEntry) {
  await updateDoc(doc(db, "bookings", bookingId), {
    ...normalizeBookingDates(patch),
    updatedAtServer: serverTimestamp(),
    editHistory: arrayUnion({ ...historyEntry, at: new Date().toISOString() }),
  });
}

/**
 * Saves a completed booking (no payment gateway yet — this is the record
 * created at "Confirm & Pay" time, with status: "pending_payment" and
 * paymentStatus: "unpaid").
 *
 * Runs inside a Firestore transaction: for every round (course + date +
 * time), it checks the slot's current headcount against that course's
 * configured capacity for that time. If ANY round is full, the whole
 * booking is rejected and nothing is written — no partial bookings.
 * @param {Record<string, any>} payload
 */

export async function createBooking(payload, { bookingId } = {}) {
  const cleanPayload = stripUndefined(
    normalizeBookingDates({
      status: "pending_payment",
      paymentStatus: "unpaid",
      ...payload,
    }),
  );

  const bookingRef = bookingId
    ? doc(db, BOOKINGS_COLLECTION, bookingId)
    : doc(collection(db, BOOKINGS_COLLECTION));

  let finalized = null; // set when the booking is already paid/confirmed

  await runTransaction(db, async (tx) => {
    finalized = null; // transactions can retry, so reset each attempt

    // ---- 1. ALL READS FIRST (Firestore requirement) ----
    const existingSnap = bookingId ? await tx.get(bookingRef) : null;
    const existing = existingSnap?.exists() ? existingSnap.data() : null;

    if (existing) {
      if (
        existing.userId &&
        cleanPayload.userId &&
        existing.userId !== cleanPayload.userId
      ) {
        throw new Error("This booking does not belong to you.");
      }
      if (
        existing.paymentStatus === "paid" ||
        existing.status === "confirmed"
      ) {
        finalized = { id: bookingRef.id, ...existing };
        return; // never touch a paid booking
      }
      if (existing.status === "cancelled") {
        throw new Error("This booking was cancelled. Please start a new one.");
      }
    }

    const oldSlots = existing ? collectSlots(existing) : new Map();
    const newSlots = collectSlots(cleanPayload);

    const reads = [];
    for (const id of new Set([...oldSlots.keys(), ...newSlots.keys()])) {
      const ref = doc(db, SLOT_BOOKINGS_COLLECTION, id);
      reads.push({ id, ref, snap: await tx.get(ref) });
    }

    // ---- 2. VALIDATE before writing anything ----
    const slotWrites = [];
    for (const { id, ref, snap } of reads) {
      const oldN = oldSlots.get(id)?.count || 0;
      const newSlot = newSlots.get(id);
      const newN = newSlot?.count || 0;
      const booked = snap.exists() ? snap.data().bookedCount || 0 : 0;
      const next = Math.max(0, booked - oldN + newN);

      // Only enforce capacity when this save adds players to the slot.
      if (newSlot?.capacity != null && newN > oldN && next > newSlot.capacity) {
        throw new Error(
          `The ${newSlot.time} slot at ${newSlot.name || "this course"} doesn't have room for ${newN} player(s) — please pick a different time.`,
        );
      }

      if (next !== booked || !snap.exists()) {
        const meta = newSlot || oldSlots.get(id);
        slotWrites.push({ ref, meta, next });
      }
    }

    // ---- 3. WRITES ----
    tx.set(bookingRef, {
      ...cleanPayload,
      // Keep fields the backend set on an existing draft.
      ...(existing?.paymentIntentId
        ? { paymentIntentId: existing.paymentIntentId }
        : {}),
      createdAtServer: existing?.createdAtServer ?? serverTimestamp(),
      ...(existing ? { updatedAtServer: serverTimestamp() } : {}),
    });

    for (const { ref, meta, next } of slotWrites) {
      tx.set(ref, {
        courseId: meta.courseId,
        date: meta.dateStr,
        time: meta.time,
        bookedCount: next,
        updatedAt: serverTimestamp(),
      });
    }
  });

  return finalized || { id: bookingRef.id, ...cleanPayload };
}

/**
 * Fetches booked-player counts for every slot of a given course on a given
 * date, so the tee-time picker can grey out full slots before checkout.
 * @param {string} courseId
 * @param {string|Date} date
 * @returns {Promise<Record<string, number>>} time -> bookedCount
 */
export async function getSlotBookingsForCourseDate(courseId, date) {
  if (!courseId || !date) return {};
  const dateStr = toDateStr(date);
  const q = query(
    collection(db, SLOT_BOOKINGS_COLLECTION),
    where("courseId", "==", courseId),
    where("date", "==", dateStr),
  );
  const snap = await getDocs(q);
  const map = {};
  snap.docs.forEach((d) => {
    const data = d.data();
    map[data.time] = data.bookedCount || 0;
  });
  return map;
}

/**
 * Fetches one page of a user's bookings, most recent first.
 * @param {string} userId
 * @param {{ pageSize?: number, cursor?: any }} options
 * @returns {Promise<{ bookings: any[], nextCursor: any, hasMore: boolean }>}
 */
export async function getUserBookings(
  userId,
  { pageSize = 6, cursor = null } = {},
) {
  if (!userId) return { bookings: [], nextCursor: null, hasMore: false };

  const constraints = [
    where("userId", "==", userId),
    orderBy("createdAtServer", "desc"),
  ];
  if (cursor) constraints.push(startAfter(cursor));
  // Fetch one extra doc so we can tell whether there's a next page.
  constraints.push(limit(pageSize + 1));

  const q = query(collection(db, BOOKINGS_COLLECTION), ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > pageSize;
  const pageDocs = snap.docs.slice(0, pageSize);
  const bookings = pageDocs.map((d) => ({ id: d.id, ...d.data() }));
  const nextCursor = hasMore ? pageDocs[pageDocs.length - 1] : null;

  return { bookings, nextCursor, hasMore };
}

/**
 * Fetches one page of ALL bookings for the admin dashboard, most recent
 * first, optionally filtered by booking status ("pending_payment" |
 * "confirmed" | "cancelled"). Uses the same "fetch one extra doc" trick as
 * getUserBookings to know if there's a next page.
 * @param {{ pageSize?: number, cursor?: any, status?: string|null }} options
 */
export async function getAllBookingsAdmin({
  pageSize = 10,
  cursor = null,
  status = null, // "pending_payment" | "confirmed" | "cancelled" | "all"/null
} = {}) {
  const constraints = [];
  if (status && status !== "all") {
    constraints.push(where("status", "==", status));
  }
  constraints.push(orderBy("createdAtServer", "desc"));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));

  const q = query(collection(db, BOOKINGS_COLLECTION), ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > pageSize;
  const pageDocs = snap.docs.slice(0, pageSize);
  const bookings = pageDocs.map((d) => ({ id: d.id, ...d.data() }));
  const nextCursor = hasMore ? pageDocs[pageDocs.length - 1] : null;

  return { bookings, nextCursor, hasMore };
}

/** Every booking, newest first, for admin search and status filtering. */
export async function getEveryBookingAdmin() {
  const snap = await getDocs(
    query(collection(db, BOOKINGS_COLLECTION), orderBy("createdAtServer", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getBookingById(bookingId) {
  if (!bookingId) return null;
  const snap = await getDoc(doc(db, BOOKINGS_COLLECTION, bookingId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Updates a booking's fulfillment STATUS from the admin panel — separate
 * from payment. Use this for "Confirm Booking" / "Cancel Booking".
 * @param {string} bookingId
 * @param {string} status - "pending_payment" | "confirmed" | "cancelled"
 */
export async function updateBookingStatus(bookingId, status) {
  if (!bookingId) throw new Error("bookingId is required.");
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    status,
    updatedAtServer: serverTimestamp(),
  });
}

/**
 * Updates a booking's PAYMENT status from the admin panel — separate from
 * fulfillment status. Use this for "Mark as Paid".
 * @param {string} bookingId
 * @param {string} paymentStatus - "unpaid" | "paid"
 */
export async function updatePaymentStatus(bookingId, paymentStatus) {
  if (!bookingId) throw new Error("bookingId is required.");
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    paymentStatus,
    updatedAtServer: serverTimestamp(),
  });
}

/**
 * Builds an invoice-shaped object from a booking, purely in memory.
 * No Firestore write — this is admin-only tooling, so there's nothing to
 * persist; the PDF is generated fresh from live booking data each time.
 * @param {Record<string, any>} booking
 */
export function buildInvoiceFromBooking(booking) {
  if (!booking?.id) throw new Error("A booking (with id) is required.");
  return {
    bookingId: booking.id,
    reservationNumber: booking.reservationNumber || "",
    invoiceNumber: `INV-${booking.id.slice(0, 8).toUpperCase()}`,
    customerName: booking.customerName || "",
    customerEmail: booking.customerEmail || "",
    selectedCourses: booking.selectedCourses || [],
    roundDates: booking.roundDates || [],
    roundTimes: booking.roundTimes || [],
    selectedAccommodation: booking.selectedAccommodation || null,
    checkInDate: booking.checkInDate || null,
    checkInTime: booking.checkInTime || null,
    checkOutDate: booking.checkOutDate || null,
    selectedFerry: booking.selectedFerry || null,
    selectedFerryTime: booking.selectedFerryTime || null,
    selectedOutboundFerryTime: booking.selectedOutboundFerryTime || null,
    selectedTransport: booking.selectedTransport || null,
    selectedTransportTime: booking.selectedTransportTime || null,
    selectedOutboundTransport: booking.selectedOutboundTransport || null,
    selectedOutboundTransportTime:
      booking.selectedOutboundTransportTime || null,
    selectedPackage: booking.selectedPackage || null,
    numPlayers: Number(booking.numPlayers ?? 1),
    total: Number(booking.grandTotal ?? 0),
  };
}

/**
 * Paid bookings are blocked by default so money isn't orphaned without a
 * refund — pass { force: true } to override after handling that.
 * @param {string} bookingId
 * @param {{ force?: boolean }} options
 * @returns {Promise<{ id: string }>}
 */
export async function deleteBooking(bookingId, { force = true } = {}) {
  if (!bookingId) throw new Error("bookingId is required.");

  const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
  let refund = { paid: false, amount: 0 };
  await runTransaction(db, async (tx) => {
     refund = { paid: false, amount: 0 }; // reset on transaction retry

    // ---- 1. ALL READS FIRST ----
    const bookingSnap = await tx.get(bookingRef);
    if (!bookingSnap.exists()) {
      throw new Error("Booking not found — it may already have been deleted.");
    }
    const booking = bookingSnap.data();
    refund = getRefundInfo(booking);

    if (!force && refund.paid) {
      throw new Error(
        "This booking is paid. Refund it first, or delete with force.",
      );
    }

    const slots = collectSlots(booking);
    const reads = [];
    for (const [id, slot] of slots) {
      const ref = doc(db, SLOT_BOOKINGS_COLLECTION, id);
      reads.push({ ref, slot, snap: await tx.get(ref) });
    }

    // ---- 2. WRITES ----
    for (const { ref, slot, snap } of reads) {
      if (!snap.exists()) continue; // nothing to release
      const booked = snap.data().bookedCount || 0;
      const next = Math.max(0, booked - slot.count);

      if (next === 0) {
        tx.delete(ref);
      } else {
        tx.update(ref, { bookedCount: next, updatedAt: serverTimestamp() });
      }
    }

    tx.delete(bookingRef);
  });

  return { id: bookingId };
}

/** Single source of truth for "how much must be refunded if this is deleted". */
export function getRefundInfo(booking) {
  const paid = booking?.paymentStatus === "paid";
  return { paid, amount: paid ? Number(booking.grandTotal ?? 0) : 0 };
}
